import { useEffect, useMemo, useRef, useState } from 'react';
import { StepPhase } from './useStepNavigation';

export type CameraAvailability = 'baked' | 'manual';
export type CameraMode = 'user' | 'transition' | 'follow';

interface UseCameraModeControllerOptions {
  hasBakedCamera: boolean;
  isPlaying: boolean;
  phase: StepPhase;
  currentStepIndex: number;
}

interface UseCameraModeControllerResult {
  cameraAvailability: CameraAvailability;
  cameraMode: CameraMode;
  onUserInteractionStart: () => void;
}

function resolveCameraMode(
  cameraAvailability: CameraAvailability,
  isPlaying: boolean,
  phase: StepPhase,
): CameraMode {
  if (!isPlaying) return 'user';
  if (cameraAvailability === 'baked') return 'follow';
  if (!isPlaying) return 'user';
  if (phase === 'transition') return 'transition';
  return 'user';
}

export function useCameraModeController({
  hasBakedCamera,
  isPlaying,
  phase,
  currentStepIndex,
}: UseCameraModeControllerOptions): UseCameraModeControllerResult {
  const cameraAvailability = useMemo<CameraAvailability>(
    () => (hasBakedCamera ? 'baked' : 'manual'),
    [hasBakedCamera],
  );
  const [cameraMode, setCameraMode] = useState<CameraMode>(() =>
    resolveCameraMode(cameraAvailability, isPlaying, phase),
  );
  const prevIsPlayingRef = useRef(isPlaying);
  const prevStepIndexRef = useRef(currentStepIndex);
  const prevPhaseRef = useRef(phase);

  useEffect(() => {
    const playbackStarted = !prevIsPlayingRef.current && isPlaying;
    const stepChanged = prevStepIndexRef.current !== currentStepIndex;
    const phaseChanged = prevPhaseRef.current !== phase;
    if (!isPlaying) {
      setCameraMode('user');
    } else if (playbackStarted || stepChanged || phaseChanged) {
      const nextMode = resolveCameraMode(cameraAvailability, isPlaying, phase);
      setCameraMode(nextMode);
    }

    prevIsPlayingRef.current = isPlaying;
    prevStepIndexRef.current = currentStepIndex;
    prevPhaseRef.current = phase;
  }, [cameraAvailability, currentStepIndex, isPlaying, phase]);

  return {
    cameraAvailability,
    cameraMode,
    onUserInteractionStart: () => {
      setCameraMode('user');
    },
  };
}
